'use client';
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import cx from 'classnames';
import { addDays, addMonths, differenceInCalendarMonths, endOfMonth, format, isSameMonth, isWithinInterval, parse, startOfMonth, } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import { RangeDatePickerDropdown } from '../../HavenCalendar';
import { INVENTORY_DATA_URL } from '../../HavenCalendar/constants';
import { convertDateStringFormat, convertDateToString, convertInventoryDataQueryParamsToString, createArrayWithLength, fetchInventoryData, formatMonthLabel, getAllAvailableDatesByDuration, getArrayMonth, getBreakDatesLabel, getNextAvailableDateInSeason, sortDateStringAsc, } from '../../HavenCalendar/helpers';
import LoadingIndicator from '../../LoadingIndicator';
import { MultiDurationSelector } from '../../MultiDurationSelector/MultiDurationSelector';
import OptionList from '../../OptionList/OptionList';
import { getHolidayDates } from '../../SearchEntryWidget/defaultData';
import styles from './DateSelectAlternate.module.scss';
const INITIAL_CURRENT_MONTH_INDEX = -99;
const getAllAvailableMonths = (availability, currentDate) => {
    var _a;
    const allAvailabilityDates = availability === null || availability === void 0 ? void 0 : availability.flatMap(({ dates }) => dates.map(({ date }) => parse(date, 'dd/MM/yyyy', currentDate)));
    const lastAvailableDate = allAvailabilityDates
        ? new Date(Math.max(...allAvailabilityDates.map((date) => date.getTime())))
        : undefined;
    const numberOfMonths = lastAvailableDate
        ? differenceInCalendarMonths(lastAvailableDate, currentDate) + 1
        : 24;
    return (_a = createArrayWithLength(numberOfMonths)) === null || _a === void 0 ? void 0 : _a.map((index) => addMonths(startOfMonth(currentDate), index));
};
const getAvailableDurations = (availability, selectedDurations, monthsInView, defaultDurations) => (availability === null || availability === void 0 ? void 0 : availability.length)
    ? availability
        .filter(({ dates, duration }) => {
        return dates.some(({ date }) => {
            const isDurationSelected = selectedDurations.includes(duration.toString());
            const isDateInView = monthsInView.includes(convertDateStringFormat(date));
            return isDurationSelected || isDateInView;
        });
    })
        .map(({ duration }) => ({
        title: `${duration.toString()} nights`,
        value: duration,
    }))
    : defaultDurations;
const getNextAvailableDate = (selectedDate, availableDates) => {
    var _a;
    return (((_a = availableDates === null || availableDates === void 0 ? void 0 : availableDates.map((available) => parse(available.date, 'dd/MM/yyyy', new Date()))) === null || _a === void 0 ? void 0 : _a.find((availableDate) => availableDate >= selectedDate)) || new Date());
};
export const DateSelectAlternate = ({ dates = getHolidayDates(), onChange, value, inventoryDataUrl = INVENTORY_DATA_URL, isLeadingPricesEnabled = false, isInventoryDrivenCalendarEnabled = false, inventoryDataQueryParams = {}, seasons: hardcodedSeasons, singleColumn = false, singleCalendar = false, isAnyDaySelectable, disableUnusableDates = false, selectedDurations, setSelectedDurations, listView = false, showSoldOuts = false, enableDepositInfo = false, showRelativePricing = false, openOnDate, }) => {
    const [availability, setAvailability] = useState(undefined);
    const [leadingPrices, setLeadingPrices] = useState(undefined);
    const [seasons, setSeasons] = useState(hardcodedSeasons);
    const [currentMonthIndex, setCurrentMonthIndex] = useState(INITIAL_CURRENT_MONTH_INDEX);
    const [isLeadingPricesLoading, setIsLeadingPricesLoading] = useState(false);
    const [arrivalDateState, setArrivalDateState] = useState(value === null || value === void 0 ? void 0 : value.date[0]);
    const [departureDateState, setDepartureDateState] = useState(value === null || value === void 0 ? void 0 : value.date[1]);
    const [focusDate, setFocusDate] = useState((value === null || value === void 0 ? void 0 : value.date[0]) || openOnDate || getNextAvailableDateInSeason(seasons, new Date()));
    const isInitialCurrentMonthIndex = currentMonthIndex === INITIAL_CURRENT_MONTH_INDEX;
    const today = useMemo(() => new Date(), []);
    const queryParams = useMemo(() => {
        return convertInventoryDataQueryParamsToString(inventoryDataQueryParams, selectedDurations);
    }, [inventoryDataQueryParams, selectedDurations]);
    useEffect(() => {
        const setAvailableDurationsData = () => __awaiter(void 0, void 0, void 0, function* () {
            setIsLeadingPricesLoading(true);
            try {
                const { leadingPrices: leadingPricesResponse, availability: availabilityResponse, seasons: seasonsResponse, error, } = yield fetchInventoryData({
                    inventoryDataUrl,
                    queryParams,
                });
                if (error) {
                    setAvailability([]);
                    setLeadingPrices([]);
                    return;
                }
                if (!hardcodedSeasons && (seasonsResponse === null || seasonsResponse === void 0 ? void 0 : seasonsResponse.length)) {
                    setSeasons(seasonsResponse.map(({ start, end }) => ({
                        start: new Date(start),
                        end: new Date(end),
                    })));
                }
                if ((availabilityResponse === null || availabilityResponse === void 0 ? void 0 : availabilityResponse.length) && (isInventoryDrivenCalendarEnabled || listView)) {
                    const sortedAvailabilityDates = availabilityResponse.map((item) => (Object.assign(Object.assign({}, item), { dates: item.dates
                            .filter(({ availableBreaks }) => !showSoldOuts ? availableBreaks && availableBreaks > 0 : true)
                            .sort(sortDateStringAsc) })));
                    setAvailability(sortedAvailabilityDates);
                }
                if ((leadingPricesResponse === null || leadingPricesResponse === void 0 ? void 0 : leadingPricesResponse.length) && (isLeadingPricesEnabled || listView)) {
                    const sortedLeadingPrices = leadingPricesResponse === null || leadingPricesResponse === void 0 ? void 0 : leadingPricesResponse.filter(({ price }) => (!showSoldOuts ? price : true)).sort(sortDateStringAsc);
                    setLeadingPrices(sortedLeadingPrices);
                }
            }
            catch (error) {
                setAvailability([]);
                setLeadingPrices([]);
            }
            finally {
                setIsLeadingPricesLoading(false);
            }
        });
        const shouldSetAvailableDurationsData = isLeadingPricesEnabled || isInventoryDrivenCalendarEnabled || listView;
        if (shouldSetAvailableDurationsData) {
            void setAvailableDurationsData();
        }
    }, [
        isInventoryDrivenCalendarEnabled,
        listView,
        isLeadingPricesEnabled,
        inventoryDataUrl,
        queryParams,
        showSoldOuts,
        hardcodedSeasons,
    ]);
    useEffect(() => {
        const notifyChange = () => {
            onChange({
                date: [
                    ...(arrivalDateState
                        ? [arrivalDateState, ...(departureDateState ? [departureDateState] : [])]
                        : []),
                ],
                nights: selectedDurations.map((duration) => Number(duration)),
            });
        };
        notifyChange();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [arrivalDateState, departureDateState, selectedDurations]);
    const onDurationChange = (durations) => {
        setSelectedDurations(durations);
        setArrivalDateState(undefined);
        setDepartureDateState(undefined);
    };
    const onRangeDatePickerDropDownChange = (date, price) => {
        var _a;
        onChange({
            nights: (_a = selectedDurations.map((duration) => Number(duration))) !== null && _a !== void 0 ? _a : null,
            date: date || [],
            price,
        });
    };
    const allAvailableMonths = useMemo(() => (availability ? getAllAvailableMonths(availability, today) : []), [availability, today]);
    const availableDates = useMemo(() => getAllAvailableDatesByDuration({
        availability,
        selectedDurations,
    }), [availability, selectedDurations]);
    const handleSetCurrentMonthIndex = (date, availableMonths) => {
        const monthIndex = availableMonths.findIndex((availableMonth) => isSameMonth(availableMonth, date));
        setCurrentMonthIndex(monthIndex === -1 ? 0 : monthIndex);
    };
    // On first load find the current month index and set it - so that the calendar loads in the correct place
    const initialArrivalDate = value === null || value === void 0 ? void 0 : value.date[0];
    useEffect(() => {
        if (isInitialCurrentMonthIndex && (allAvailableMonths === null || allAvailableMonths === void 0 ? void 0 : allAvailableMonths.length)) {
            const initialCalendarDate = getNextAvailableDate(startOfMonth(initialArrivalDate || new Date()), availableDates);
            handleSetCurrentMonthIndex(initialCalendarDate, allAvailableMonths);
        }
    }, [allAvailableMonths, availableDates, initialArrivalDate, isInitialCurrentMonthIndex]);
    const monthsInView = useMemo(() => getArrayMonth(focusDate, singleCalendar, 0).map((date) => convertDateToString(date, 'yyyy-MM')), [focusDate, singleCalendar]);
    const availableDurations = useMemo(() => getAvailableDurations(availability, selectedDurations, monthsInView, dates), [availability, dates, monthsInView, selectedDurations]);
    const currentMonth = useMemo(() => {
        return !isInitialCurrentMonthIndex && (allAvailableMonths === null || allAvailableMonths === void 0 ? void 0 : allAvailableMonths.length)
            ? allAvailableMonths[currentMonthIndex]
            : undefined;
    }, [allAvailableMonths, currentMonthIndex, isInitialCurrentMonthIndex]);
    const nextAvailableDate = getNextAvailableDate(currentMonth ? endOfMonth(currentMonth) : new Date(), availableDates);
    if (listView && !availability) {
        return (<div className={styles.loadingIndicator}>
        <LoadingIndicator loading={true} size='default'/>
      </div>);
    }
    if (listView && (availability === null || availability === void 0 ? void 0 : availability.length) && currentMonth) {
        const listViewMonthsInView = [format(currentMonth, 'yyyy-MM')];
        const listViewAvailableDurations = getAvailableDurations(availability, selectedDurations, listViewMonthsInView, dates);
        const listViewAvailability = availability === null || availability === void 0 ? void 0 : availability.find(({ duration }) => Number(selectedDurations[0]) === duration);
        const listViewOptions = listViewAvailability === null || listViewAvailability === void 0 ? void 0 : listViewAvailability.dates.filter(({ date }) => isSameMonth(parse(date, 'dd/MM/yyyy', today), currentMonth)).map(({ date, availableBreaks }) => {
            var _a;
            const price = (_a = leadingPrices === null || leadingPrices === void 0 ? void 0 : leadingPrices.find((price) => price.date === date)) === null || _a === void 0 ? void 0 : _a.price;
            const isSoldOut = !availableBreaks;
            return {
                label: getBreakDatesLabel(parse(date, 'dd/MM/yyyy', today), Number(selectedDurations[0])),
                subLabel: generateListViewSubLabel(isLeadingPricesLoading, price, isSoldOut),
                value: date,
                disabled: isSoldOut,
            };
        });
        const disableNext = !isInitialCurrentMonthIndex && currentMonthIndex >= (allAvailableMonths === null || allAvailableMonths === void 0 ? void 0 : allAvailableMonths.length) - 1;
        const disabledPrev = !isInitialCurrentMonthIndex && currentMonthIndex <= 0;
        const toggleCalendarMonth = (change) => {
            setCurrentMonthIndex((value) => value + change);
        };
        const onListViewDateChange = (dateString) => {
            const date = parse(dateString, 'dd/MM/yyyy', today);
            setArrivalDateState(date);
            setDepartureDateState(addDays(date, Number(selectedDurations[0])));
        };
        const isCurrentMonthInSeason = seasons === null || seasons === void 0 ? void 0 : seasons.some(({ start, end }) => isWithinInterval(endOfMonth(currentMonth), {
            start,
            end,
        }));
        return (<>
        <div className={styles.toggleMonthRoot}>
          <button className={cx(styles.toggleMonthButton, styles.toggleMonthButtonPrev)} onClick={() => toggleCalendarMonth(-1)} onKeyDown={() => toggleCalendarMonth(-1)} disabled={disabledPrev}>
            <span className='srOnly'>Previous Month</span>
          </button>
          <div className={styles.month}>{formatMonthLabel(currentMonth)}</div>
          <button className={cx(styles.toggleMonthButton, styles.toggleMonthButtonNext)} onClick={() => toggleCalendarMonth(1)} onKeyDown={() => toggleCalendarMonth(1)} disabled={disableNext}>
            <span className='srOnly'>Next Month</span>
          </button>
        </div>
        <MultiDurationSelector dates={listViewAvailableDurations} setSelectedDurations={onDurationChange} selectedDurations={selectedDurations}/>
        <div className={styles.listView}>
          {(listViewOptions === null || listViewOptions === void 0 ? void 0 : listViewOptions.length) ? (<div className={styles.options}>
              <OptionList options={listViewOptions} groupName='CalendarListDates' defaultValue={arrivalDateState ? format(arrivalDateState, 'dd/MM/yyyy') : undefined} onChange={onListViewDateChange}/>
            </div>) : (<NoAvailability otherDurationsAvailable={listViewAvailableDurations && listViewAvailableDurations.length > 1} inSeason={isCurrentMonthInSeason} nextAvailableDate={nextAvailableDate} onNextAvailableDateClick={(date) => handleSetCurrentMonthIndex(date, allAvailableMonths)}/>)}
        </div>
      </>);
    }
    return (<>
      <div className={styles.buttonSelectRoot}>
        <MultiDurationSelector dates={availableDurations} setSelectedDurations={onDurationChange} selectedDurations={selectedDurations}/>
        <div className={styles.buttonSelectDivider}/>
        <RangeDatePickerDropdown startDate={(value === null || value === void 0 ? void 0 : value.date) ? value.date[0] : undefined} endDate={(value === null || value === void 0 ? void 0 : value.date) ? value.date[1] : undefined} onChange={({ fromDate: from, endDate: end, price }) => onRangeDatePickerDropDownChange([from, end], price)} minDate={today} seasons={seasons} duration={Number(selectedDurations[0])} singleColumn={singleColumn} singleCalendar={singleCalendar} showRelativePricing={showRelativePricing} isAnyDaySelectable={isAnyDaySelectable} disableUnusableDates={disableUnusableDates} availableDates={availableDates || []} leadingPrices={(leadingPrices === null || leadingPrices === void 0 ? void 0 : leadingPrices.length) ? leadingPrices : undefined} focusDate={focusDate} setFocusDate={setFocusDate} enableDepositInfo={enableDepositInfo}/>
      </div>
    </>);
};
const NoAvailability = ({ otherDurationsAvailable, inSeason = false, nextAvailableDate, onNextAvailableDateClick, }) => {
    if (otherDurationsAvailable) {
        return (<div className={styles.noAvailabilityMessage}>
        <span className={styles.heading}>
          <strong>Sorry, all availability has been snapped up for this period.</strong>
        </span>
        <div className={styles.divider}/>
        <span className={styles.subHeading}>
          But we have plenty left elsewhere. Try a different number of nights.
        </span>
      </div>);
    }
    if (inSeason) {
        return (<div className={styles.noAvailabilityMessage}>
        <span className={styles.heading}>
          <strong>Sorry, all availability has been snapped up for this period.</strong>
        </span>
        <div className={styles.divider}/>
        <span className={styles.subHeading}>
          But we have plenty left elsewhere. Try a different month.
        </span>
      </div>);
    }
    return (<div className={styles.noAvailabilityMessage}>
      <span className={styles.heading}>
        We&apos;d love to see you but are <strong>closed for Winter</strong> to make our parks even
        better.
      </span>
      <div className={styles.divider}/>
      {nextAvailableDate && onNextAvailableDateClick && (<button className={styles.nextAvailableButton} onClick={() => onNextAvailableDateClick(nextAvailableDate)}>
          Here&apos;s when we&apos;re next open
        </button>)}
    </div>);
};
const generateListViewSubLabel = (isLoading, price, isSoldOut) => {
    if (isSoldOut) {
        return <span>Sold out</span>;
    }
    if (isLoading) {
        return <span>Loading</span>;
    }
    if (price) {
        return (<span>
        from <strong>£{price}</strong>
      </span>);
    }
    return <></>;
};
DateSelectAlternate.displayName = 'DateSelectAlternate';
export default DateSelectAlternate;
